<template>
  <div class="component-wrapper">
    <!-- 查询表单容器：拆分为条件行 + 按钮行 -->
    <div class="query-form-container">
      <!-- 第一行：查询条件（充满整行） -->
      <div class="query-form-row">
        <div class="form-item">
          <label class="form-label">厂商名称</label>
          <a-input v-model:value="formModel.companyName" placeholder="请输入厂商名称" clearable />
        </div>
        <div class="form-item">
          <label class="form-label">捐赠人类型</label>
          <my-dropdown-checkbox v-model:checkedList="formModel.donorType" :options="donorSegmentationOptions"
            :label-formatter="(checked, total) => (total > 0 && checked === total) ? '全选' : `已选 ${checked}/${total}`" />
        </div>
        <div class="form-item">
          <label class="form-label">测评通过时间</label>
          <my-dropdown-checkbox v-model:checkedList="formModel.latestEvaluationTime" :options="yearList"
            :label-formatter="(checked, total) => (total > 0 && checked === total) ? '全选' : `已选 ${checked}/${total}`" />
        </div>
      </div>

      <!-- 第二行：按钮居中 -->
      <div class="query-button-row">
        <a-button type="primary" @click="handleQuery">查询</a-button>
        <a-button @click="handleReset" style="margin-left: 8px;">重置</a-button>
      </div>
    </div>

    <!-- 表格区域 -->
    <div class="table-wrapper">
      <a-table :columns="tableColumns" :data-source="tableData" :pagination="pagination" bordered size="middle"
        :loading="tableLoading" row-key="companyName" @change="handleTableChange">
        <template #bodyCell="{ column, record }">
          <div v-if="column.key === 'donorType'">
            {{ record.donorType ?? '-' }}
          </div>
          <div v-else>
            {{ record[column.dataIndex] }}
          </div>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue';
import axios from 'axios';
import MyDropdownCheckbox from '../components/MyDropdownCheckbox.vue';
import { notification } from 'ant-design-vue';

// 年份列表
const yearList = ref(['2021', '2022', '2023', '2024', '2025', '2026']);
const donorSegmentationOptions = [
  'A类捐赠人',
  'B类捐赠人',
  'C类捐赠人',
  '特殊捐赠人',
  '非捐赠人'
];

// 表单模型（默认全选）
const formModel = reactive({
  companyName: '',
  donorType: [...donorSegmentationOptions],
  latestEvaluationTime: [...yearList.value]
});

// 分页配置
const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
  showSizeChanger: true,
  showTotal: (total) => `共 ${total} 条`
});

// 表格加载状态
const tableLoading = ref(false);

// 表格数据
const tableData = ref([]);

// 表格列配置（添加排序功能）
const tableColumns = [
  { title: '厂商名称', dataIndex: 'companyName', key: 'companyName', width: 150 },
  {
    title: '最新通过测评时间',
    dataIndex: 'latestEvaluationTime',
    key: 'latestEvaluationTime',
    width: 160
  },
  {
    title: '通过兼容性产品数量',
    dataIndex: 'productCount',
    key: 'productCount',
    width: 180
  },
  { title: '捐赠人类型', dataIndex: 'donorType', key: 'donorType', width: 150 }
];

// 当用户在表格中尝试修改某行的捐赠人类型时，先弹窗确认；确认后再提交到后端
// 捐赠人类型修改：必须用 form-data（URLSearchParams），不能用 JSON，否则后端接收不到参数。
// Inline editing removed from table; confirmChange and update API removed.

// 重置表单
// 重置表单：多选项需恢复全选，不能清空，否则“全部”状态丢失。
const handleReset = () => {
  formModel.companyName = '';
  formModel.donorType = [...donorSegmentationOptions];
  formModel.latestEvaluationTime = [...yearList.value];
  pagination.current = 1;
  tableData.value = [];
  pagination.total = 0;
};


// 查询请求
// 查询接口：多选参数需序列化为重复 key，不能用逗号分隔，否则后端无法绑定 List。
const fetchTableData = async () => {
  try {
    tableLoading.value = true;
    const requestBody = {
      productName: formModel.companyName,
      latestEvaluationTime: formModel.latestEvaluationTime,
      donorType: formModel.donorType,
      pageVO: {
        curPage: pagination.current,
        pageSize: pagination.pageSize
      }
    };
    const res = await axios.post('/contributorController/getOpCompatibilityTestingVendorInfoList', requestBody);

    if (res.data?.baseResult?.code !== 200) {
      throw new Error(res.data.desc || '查询失败');
    }

    tableData.value = res.data.baseResult.data || [];
    pagination.pageSize = res.data?.pageVO?.pageSize || pagination.pageSize;
    pagination.current = res.data?.pageVO?.curPage || pagination.current;
    pagination.total = res.data?.pageVO?.totalRows || 0;
  } catch (error) {
    console.error('查询失败：', error);
    tableData.value = [];
    notification.error({
      message: '查询失败',
      description: error?.message || '请稍后再试'
    });
  } finally {
    tableLoading.value = false;
  }
};

const handleQuery = () => {
  pagination.current = 1;
  fetchTableData();
};

const handleTableChange = (pager) => {
  pagination.current = pager?.current || 1;
  pagination.pageSize = pager?.pageSize || pagination.pageSize;
  fetchTableData();
};

onMounted(() => {
  handleQuery();
});
</script>

<style scoped>
.component-wrapper {
  width: 100%;
  padding: 16px;
  box-sizing: border-box;
}

/* 查询表单容器 */
.query-form-container {
  text-align: left;
  width: 100%;
  margin-bottom: 16px;
}

/* 核心：查询条件行 - 强制一行 + 充满整行 */
.query-form-row {
  display: flex;
  width: 100%;
  gap: 16px;
  /* 每个查询项之间的间距 */
  flex-wrap: nowrap;
  /* 禁止部分换行！核心 */
  overflow-x: auto;
  /* 小屏时横向滚动，避免错乱 */
  align-items: center;
  /* 标签和输入框垂直居中对齐 */
  padding-bottom: 8px;
}

/* 每个查询项：均匀分配宽度，充满整行 */
.form-item {
  min-width: 180px;
  /* 最小宽度，避免挤太窄 */
  display: flex;
  align-items: center;
}

/* 标签样式 */
.form-label {
  font-size: 14px;
  color: #333;
  margin-right: 8px;
  white-space: nowrap;
  /* 标签不换行 */
}

/* 输入框/下拉框：充满所在项的宽度 */
.form-item :deep(.ant-input),
.form-item :deep(.ant-select) {
  width: 100% !important;
  /* 强制充满 */
}

/* 按钮行 */
.query-button-row {
  margin-top: 16px;
  display: flex;
  justify-content: center;
}

/* 表格区域 */
.table-wrapper {
  width: 100%;
}
</style>